import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, qs } from "@/lib/api";
import type { Contact } from "@/lib/types";
import { brandById } from "@/lib/brands";
import { formatDateTime } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

/** Email one person a saved email (crm-email's email_send_one: preview first, then confirm). */
export function SendEmailDialog({ c, onClose }: { c: Contact; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [brand, setBrand] = useState<number | "">(c.brands[0]?.brand_id ?? "");
  const [q, setQ] = useState("");
  const [emailId, setEmailId] = useState<number | "">("");
  const [preview, setPreview] = useState<{ subject?: string; from?: string; to?: string } | null>(null);
  const emails = useQuery({
    queryKey: ["email-list", brand, q],
    queryFn: () =>
      api<{ emails: { id: number; brand_id: number; name: string; subject: string; kind: string; label?: string }[] }>("/api/v1/emails" + qs({ brand_id: brand || undefined, q: q || undefined, limit: 50 })),
  });
  const send = useMutation({
    mutationFn: async (confirm: boolean) => {
      try {
        return await api(`/api/v1/emails/${emailId}/send-one`, { json: { contact_id: c.id, confirm } });
      } catch (e) {
        if (e instanceof ApiError && e.code === "confirm_required") {
          setPreview(e.details as { subject?: string; from?: string; to?: string });
          return null;
        }
        throw e;
      }
    },
    onSuccess: (r) => {
      if (r) {
        toast("Email queued to send");
        void qc.invalidateQueries({ queryKey: ["timeline", c.id] });
        onClose();
      }
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>Email {c.first_name ?? "this person"}</DialogTitle>
        <DialogDescription>Sends one of your saved emails to this person only, with their name filled in. It's tracked and shows on their timeline.</DialogDescription>
        {!preview ? (
          <div className="space-y-3">
            <div className="grid grid-cols-[8rem_1fr] gap-2">
              <Select value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")} aria-label="Brand">
                <option value="">Any brand</option>
                {[1, 2, 3, 4, 5].map((b) => (
                  <option key={b} value={b}>
                    {brandById(b)?.short}
                  </option>
                ))}
              </Select>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an email by name or subject" aria-label="Find an email" />
            </div>
            <div className="max-h-64 overflow-y-auto rounded-md border" role="radiogroup" aria-label="Email to send">
              {(emails.data?.emails ?? []).length === 0 && <p className="p-3 text-sm text-[var(--color-muted-foreground)]">No saved emails match.</p>}
              {(emails.data?.emails ?? []).map((e) => (
                <label key={e.id} className="flex cursor-pointer items-start gap-2 border-b px-3 py-2 text-sm last:border-0 hover:bg-[var(--color-muted)]">
                  <input type="radio" name="email" className="mt-1 accent-[var(--color-primary)]" checked={emailId === e.id} onChange={() => setEmailId(e.id)} />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {e.name} {e.label && <span className="text-xs font-normal text-[var(--color-muted-foreground)]">({e.label})</span>}
                    </span>
                    <span className="block truncate text-xs text-[var(--color-muted-foreground)]">
                      {brandById(e.brand_id)?.short}: {e.subject}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!emailId || send.isPending} onClick={() => send.mutate(false)}>
                Review…
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <dl className="grid grid-cols-[5rem_1fr] gap-y-1">
              <dt className="text-[var(--color-muted-foreground)]">To</dt>
              <dd>{preview.to}</dd>
              <dt className="text-[var(--color-muted-foreground)]">From</dt>
              <dd>{preview.from}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Subject</dt>
              <dd>{preview.subject}</dd>
            </dl>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPreview(null)}>
                Back
              </Button>
              <Button disabled={send.isPending} onClick={() => send.mutate(true)}>
                Send it
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Start this person in a published automation (crm-automations' automations_enroll). */
export function StartAutomationDialog({ c, onClose }: { c: Contact; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [id, setId] = useState<number | "">("");
  const list = useQuery({
    queryKey: ["automations-list"],
    queryFn: () => api<{ automations: { id: number; name: string; status: string; brand_id: number | null; live_count: number }[] }>("/api/v1/automations?limit=500"),
  });
  const startable = (list.data?.automations ?? []).filter((a) => a.status === "active" || a.status === "shadow");
  const chosen = startable.find((a) => a.id === id);
  const enroll = useMutation({
    mutationFn: () => api<{ started: { contact_id: number }[]; skipped: { contact_id: number; reason?: string }[] }>(`/api/v1/automations/${id}/enroll`, {
        // The click on "Start automation" is the OK: a live automation sends for real, so the API asks for a confirm
        // (go-live audit H-2); a signed-in user may send it directly.
        json: { contact_ids: [c.id], confirm: true },
      }),
    onSuccess: (r) => {
      if (r.started.length) toast(`Started in ${chosen?.name}${chosen?.status === "shadow" ? " (shadow: logs only, sends nothing)" : ""}`);
      else toast(`Not started: ${r.skipped[0]?.reason ?? "already in it"}`, "error");
      void qc.invalidateQueries({ queryKey: ["automation-positions", c.id] });
      void qc.invalidateQueries({ queryKey: ["timeline", c.id] });
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>Start an automation</DialogTitle>
        <DialogDescription>
          They begin at its first step now and, in a live automation, get its emails for real. If they're already in it, nothing changes.
        </DialogDescription>
        <div className="space-y-3">
          <Select value={id} onChange={(e) => setId(e.target.value ? Number(e.target.value) : "")} aria-label="Automation">
            <option value="">Pick an automation…</option>
            {startable.map((a) => (
              <option key={a.id} value={a.id}>
                {a.brand_id ? `${brandById(a.brand_id)?.short}: ` : ""}
                {a.name}
                {a.status === "shadow" ? " (shadow: logs only)" : ""}
              </option>
            ))}
          </Select>
          {list.data && startable.length === 0 && <p className="text-sm text-[var(--color-muted-foreground)]">No automation is live or in shadow mode yet.</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!id || enroll.isPending} onClick={() => enroll.mutate()}>
              Start automation
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Email a sign-in link for the Club, Manager Training or courses (crm-sites' logins_link_send, via Postmark). */
export function LoginLinkDialog({ c, onClose }: { c: Contact; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const sites = useQuery({ queryKey: ["sites"], queryFn: () => api<{ sites: { site: string; name: string; access_keys: string[] }[] }>("/api/v1/sites") });
  const status = useQuery({
    queryKey: ["login-status", c.id],
    queryFn: () => api<{ has_password?: boolean; password_source?: string; last_login_at?: string | null; locked_until?: string | null }>(`/api/v1/logins/status/${c.id}`),
    retry: false,
  });
  const signIn = (sites.data?.sites ?? []).filter((s) => s.access_keys?.length);
  const [site, setSite] = useState("");
  const [purpose, setPurpose] = useState("login");
  const [next, setNext] = useState("");
  const send = useMutation({
    mutationFn: () => api<{ sent: boolean; to: string; expires_at: string | null }>("/api/v1/logins/links/send", { json: { site: site || signIn[0]?.site, contact_id: c.id, purpose, next: next || undefined } }),
    onSuccess: (r) => {
      toast(r.sent ? `Sign-in link sent to ${r.to}${r.expires_at ? `, works until ${formatDateTime(r.expires_at)}` : ""}` : "Not sent", r.sent ? "ok" : "error");
      void qc.invalidateQueries({ queryKey: ["timeline", c.id] });
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  const s = status.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>Send a sign-in link</DialogTitle>
        <DialogDescription>
          Emails {c.first_name ?? "them"} a link that signs them in (through Postmark, to their primary address). It's noted on their timeline.
        </DialogDescription>
        <div className="space-y-3 text-sm">
          {s && (
            <p className="text-xs text-[var(--color-muted-foreground)]">
              {s.has_password ? `Has a password (${s.password_source?.replace(/_/g, " ") ?? "set"})` : "No password yet"}
              {s.last_login_at ? `; last signed in ${formatDateTime(s.last_login_at)}` : "; never signed in here"}
              {s.locked_until ? `; locked until ${formatDateTime(s.locked_until)}` : ""}.
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="ll-site">Site</Label>
              <Select id="ll-site" value={site || signIn[0]?.site || ""} onChange={(e) => setSite(e.target.value)}>
                {signIn.map((x) => (
                  <option key={x.site} value={x.site}>
                    {x.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ll-purpose">Kind of link</Label>
              <Select id="ll-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                <option value="login">Sign in (works 72 hours)</option>
                <option value="welcome">Welcome: sign in and choose a password</option>
                <option value="reset">Reset the password</option>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ll-next">Open this page after signing in (optional)</Label>
            <Input id="ll-next" value={next} onChange={(e) => setNext(e.target.value)} placeholder="/recordings" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={send.isPending || signIn.length === 0 || c.emails.length === 0} onClick={() => send.mutate()}>
              Email the link
            </Button>
          </div>
          {c.emails.length === 0 && <p className="text-xs text-[var(--color-destructive)]">They have no email address.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
